import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CustomCellClickAction,
	GridCellConfig,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { RaonColDef } from "src/types/grid";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

import {
	TQC1050_R01Dto,
	TQC1050_R01_JUDGE_OPTIONS,
} from "./Dto_TQC1050_R01";
import { useTQC1050_R01Store } from "./Store_TQC1050_R01";
import { useTQC1050_R01CellChange } from "./hooks/TQC1050_R01CellChange";

const COLUMN_STATE_KEY = "TQC1050_R01-grid-column-state";

/** 입고내역에서 온 조회값 컬럼. 편집 불가. */
const READONLY_COLUMNS = [
	"compNm", "logicCenterNm", "warehouseNm", "customCd", "customNm",
	"transportDt", "itemCd", "itemNm", "brandNm", "itemStatus", "qty", "unit",
	"inspectorEmp", "inspectorEmpNm",
];

const FIXED_HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	compNm: "사업장명",
	logicCenterNm: "물류센터",
	warehouseNm: "창고",
	customCd: "거래처",
	customNm: "상호",
	transportDt: "입고일자",
	itemCd: "품목코드",
	itemNm: "품명",
	brandNm: "브랜드",
	itemStatus: "품목등급",
	qty: "입고량",
	unit: "단위",
	judgeResult: "판정",
	remark: "조치사항",
	inspectValue1_1: "깨짐없음",
	inspectValue1_2: "갈라짐없음",
	inspectValue1_3: "찍힘없음",
	inspectValue1_4: "기름때없음",
};

const INSPECT_VALUE_FIELDS = [
	"inspectValue1_1",
	"inspectValue1_2",
	"inspectValue1_3",
	"inspectValue1_4",
] as const;

/**
 * EQM2040_R01 패턴: 그룹 구조를 유지하며 헤더 클래스를 정규화한다.
 * 그룹 헤더에는 group-header 클래스를, 리프 헤더에는 기본 헤더 클래스를 부여한다.
 */
const normalizeHeaderClass = (
	defs: (ColDef<TQC1050_R01Dto> | ColGroupDef<TQC1050_R01Dto>)[],
): (ColDef<TQC1050_R01Dto> | ColGroupDef<TQC1050_R01Dto>)[] =>
	defs.map((def: any) => {
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			const groupHeaderClass = [def.headerClass, "common-group-header"]
				.filter(Boolean).join(" ");
			return { ...def, headerClass: groupHeaderClass, suppressMenu: true, children } as ColGroupDef<TQC1050_R01Dto>;
		}
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
			.filter(Boolean).join(" ");
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true } as ColDef<TQC1050_R01Dto>;
	});

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<TQC1050_R01Dto> | null>;
	defaultTQC1050_R01Row: TQC1050_R01Dto;
}

export const Table_TQC1050_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultTQC1050_R01Row } = props;

	const {
		TQC1050_R01List,
		setTQC1050_R01List,
		updateTQC1050_R01List,
		originalTQC1050_R01List,
		selectedTQC1050_R01List,
		addSelectedTQC1050_R01List,
		removeSelectedTQC1050_R01List,
		initSelectedTQC1050_R01List,
		allSelectedTQC1050_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useTQC1050_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	/** 편집 가능한 컬럼 = 검사항목 + 판정 + 조치사항 */
	const editableColumns = useMemo(
		() => [...INSPECT_VALUE_FIELDS, "judgeResult", "remark"],
		[],
	);

	const headerNames = useMemo(() => ({ ...FIXED_HEADER_NAMES }), []);

	const cssCellConfig: GridCellConfig = useMemo(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: READONLY_COLUMNS,
		pointerCells: editableColumns,
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ["itemCd", "itemNm", "customNm", "remark"],
	}), [editableColumns]);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: TQC1050_R01List,
		defaultGridData: defaultTQC1050_R01Row,
		selectedRow: selectedTQC1050_R01List,
		originalGridData: originalTQC1050_R01List,
		updateRow: (index: number, data: TQC1050_R01Dto) => updateTQC1050_R01List(index, data),
	});

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC1050_R01Dto>({
			idKey: "valueIndex",
			selectedList: selectedTQC1050_R01List,
			deletedList: [],
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		createCellClassRules, selectedTQC1050_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	]);

	/** 검사항목 컬럼. 검사값은 일반 텍스트로 직접 입력한다. */
	const specColumn = (
		field: typeof INSPECT_VALUE_FIELDS[number],
		headerName: string,
	): RaonColDef<TQC1050_R01Dto> => ({
		headerName,
		colId: field,
		field,
		width: 110,
		editable: true,
		pasteable: true,
		cellStyle: { textAlign: "center" },
	});

	const baseColumnDefs: (ColDef<TQC1050_R01Dto> | ColGroupDef<TQC1050_R01Dto>)[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "사업장명", colId: "compNm", field: "compNm", width: 120,
			editable: false,
		},
		{
			headerName: "물류센터", colId: "logicCenterNm", field: "logicCenterNm", width: 100,
			editable: false,
		},
		{
			headerName: "창고", colId: "warehouseNm", field: "warehouseNm", width: 100,
			editable: false,
		},
		{
			headerName: "거래처", colId: "customCd", field: "customCd", width: 90,
			editable: false,
		},
		{
			headerName: "상호", colId: "customNm", field: "customNm", width: 140,
			editable: false, cellStyle: { textAlign: "left" },
		},
		{
			headerName: "입고일자", colId: "transportDt", field: "transportDt", width: 110,
			editable: false,
			valueFormatter: (params) =>
				params.data?.transportDt ? formatDate(new Date(params.data.transportDt)) : "",
			comparator: cellDateComparator,
		},
		{
			headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 130,
			editable: false, cellStyle: { textAlign: "left" },
		},
		{
			headerName: "품명", colId: "itemNm", field: "itemNm", width: 180,
			editable: false, cellStyle: { textAlign: "left" },
		},
		{
			headerName: "브랜드", colId: "brandNm", field: "brandNm", width: 100,
			editable: false,
		},
		{
			headerName: "품목등급", colId: "itemStatus", field: "itemStatus", width: 90,
			editable: false, cellStyle: { textAlign: "center" },
		},
		{
			headerName: "입고량", colId: "qty", field: "qty", width: 90,
			editable: false,
			valueFormatter: (params) => (params.value ? Number(params.value).toLocaleString() : "0"),
			cellStyle: { textAlign: "right" },
		},
		{
			headerName: "단위", colId: "unit", field: "unit", width: 70,
			editable: false, cellStyle: { textAlign: "center" },
		},
		{
			headerName: "검사항목",
			headerClass: "common-group-header",
			children: [
				specColumn("inspectValue1_1", "깨짐없음"),
				specColumn("inspectValue1_2", "갈라짐없음"),
				specColumn("inspectValue1_3", "찍힘없음"),
				specColumn("inspectValue1_4", "기름때없음"),
			],
		},
		{
			headerName: "판정", colId: "judgeResult", field: "judgeResult", width: 80,
			editable: true,
			pasteable: true,
			cellEditor: "agSelectCellEditor",
			cellEditorParams: { values: TQC1050_R01_JUDGE_OPTIONS },
			cellStyle: { textAlign: "center" },
		},
		{
			headerName: "조치사항", colId: "remark", field: "remark", width: 200,
			editable: true, pasteable: true,
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], [cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs as ColDef<TQC1050_R01Dto>[], [])
	, [baseColumnDefs]);

	const columnDefs: (ColDef<TQC1050_R01Dto> | ColGroupDef<TQC1050_R01Dto>)[] = useMemo(() => {
		const merged = GridCustomColumnDefs<TQC1050_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
		return normalizeHeaderClass(merged);
	}, [columnState, baseColumnDefs, headerNames]);

	const { handleCellChange } = useTQC1050_R01CellChange({
		updateTQC1050_R01List,
		originalTQC1050_R01List,
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: TQC1050_R01List,
		idKey: "valueIndex",
		selectedList: selectedTQC1050_R01List,
		deletedList: [],
		addSelected: addSelectedTQC1050_R01List,
		removeSelected: removeSelectedTQC1050_R01List,
		clickable, setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC1050_R01List as any,
		editableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	// 편집 가능 셀 클릭 시 바로 편집 시작
	const cellClickActions: CustomCellClickAction<TQC1050_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => editableColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], [editableColumns]);
	const handleCellClick = CreateCellClickHandler(TQC1050_R01List, "valueIndex", cellClickActions);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: "",
		compNm: `${TQC1050_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [TQC1050_R01List.length]);

	return (
		<GridContextWrapper<TQC1050_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setTQC1050_R01List}
			initSelected={initSelectedTQC1050_R01List}
			allSelected={allSelectedTQC1050_R01List}
			defaultRowData={defaultTQC1050_R01Row}
			baseColumnDefs={columnState ? columnDefs as ColDef<TQC1050_R01Dto>[] : baseColumnDefs as ColDef<TQC1050_R01Dto>[]}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<TQC1050_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<TQC1050_R01Dto>({
					idKey: "valueIndex",
					selectedList: selectedTQC1050_R01List,
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
